import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { signOut, fetchAuthSession } from "aws-amplify/auth";
import {
  AlertTriangle,
  CheckCircle,
  Edit3,
  ExternalLink,
  Sparkles,
  Store,
  Trash2,
  TrendingUp,
  Zap,
} from "lucide-react";
import "./HomePage.css";
import "./RecruiterHomePage.css";
import "./button-styles.css";
import LogoImg from "../assets/layerbound-white.png";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import { resolveMenuBaseUrl, getMenuBaseDisplay } from "../utils/menuLinks";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { getCallReadyStats } from "../utils/leadReadiness";
import CallReadinessModal from "../components/CallReadinessModal";
import { getPlanById } from "../constants/upgradePlans";

const MENU_BASE_URL = resolveMenuBaseUrl(awsConfig.menu_base_url);
const MENU_BASE_DISPLAY = getMenuBaseDisplay(awsConfig.menu_base_url);
const SLUG_API_URL = awsConfig.api_url_manage_slug;

const RECRUITER_LIMITS = {
  free: 100,
  starter: 500,
  pro: 2000,
  scale: 3000,
  enterprise: 5000,
  lifetime_studio: 1500,
  lifetime_agency: 3000,
  lifetime_enterprise: 6000,
};

const sanitizeSlugFragment = (value) => {
  if (!value) return "";
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

const getBusinessSlug = (business) => {
  if (!business) return "";
  const existing = sanitizeSlugFragment(business.slug);
  if (existing) return existing;
  const fromName = sanitizeSlugFragment(business.name);
  if (fromName) return fromName;
  const id = (business?.businessId || "").toLowerCase().trim();
  if (!id) return "biz-index";
  return `biz-${id.slice(0, 10)}`;
};

const getRecruiterLimit = (planId) => {
  if (!planId) return RECRUITER_LIMITS.free;
  const key = planId.toLowerCase();
  return RECRUITER_LIMITS[key] ?? RECRUITER_LIMITS.free;
};

const getLeadTimestamp = (lead) =>
  lead?.createdAt ||
  lead?.lead?.createdAt ||
  lead?.lead?.submittedAt ||
  lead?.created_at ||
  null;

function RecruiterHomePage({ user, business, onSignOut }) {
  usePageMetadata({
    title: "Home",
    description: "Review submissions and manage intake workflows.",
  });

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [menuPublishes, setMenuPublishes] = useState({ active: [], archived: [] });
  const [callReadinessFilter, setCallReadinessFilter] = useState("all");
  const [copiedSummaryId, setCopiedSummaryId] = useState(null);
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);
  const [showSlugEdit, setShowSlugEdit] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [slugLoading, setSlugLoading] = useState(false);
  const [slugError, setSlugError] = useState("");
  const [editingStore, setEditingStore] = useState(null);
  const [newStoreSlug, setNewStoreSlug] = useState("");
  const [deletingPublish, setDeletingPublish] = useState(null);
  const [businessSlug, setBusinessSlug] = useState("");

  const leads = useMemo(
    () => (orders || []).filter((o) => (o.type || "").toLowerCase() === "lead"),
    [orders]
  );

  const activePlanId = useMemo(
    () => (business?.plan || "free").toLowerCase(),
    [business?.plan]
  );
  const activePlan = useMemo(() => getPlanById(activePlanId), [activePlanId]);
  const planBadge = useMemo(() => {
    if (!business) return null;
    if (activePlanId === "enterprise" || activePlanId === "pro" || activePlanId === "scale") {
      return { className: "plan-badge enterprise", label: "Enterprise Plan", icon: <Sparkles size={12} /> };
    }
    if (activePlanId === "starter") {
      return { className: "plan-badge starter", label: "Starter Plan", icon: <Zap size={12} /> };
    }
    if (activePlanId.startsWith("lifetime")) {
      return {
        className: "plan-badge lifetime",
        label: activePlan?.name || "Lifetime Plan",
        icon: <Sparkles size={12} />,
      };
    }
    return { className: "plan-badge free", label: "Free Plan" };
  }, [business, activePlanId, activePlan]);
  const derivedBusinessSlug = useMemo(
    () => businessSlug || getBusinessSlug(business),
    [businessSlug, business]
  );

  const deriveStoreUrl = useCallback(
    (storeSlug) => {
      const bizSlug = derivedBusinessSlug;
      const normalizedStoreSlug = sanitizeSlugFragment(storeSlug) || "index";
      if (!bizSlug || !normalizedStoreSlug) return "";
      return `${MENU_BASE_URL}/${bizSlug}/${normalizedStoreSlug}`;
    },
    [derivedBusinessSlug]
  );

  const storeLookup = useMemo(() => {
    const map = {};
    (menuPublishes.active || []).forEach((pub) => {
      const storeId = pub.storeId || pub.store_id;
      const slug = pub.storeSlug || pub.slug;
      if (storeId) map[`id:${storeId}`] = pub;
      if (slug) map[`slug:${slug}`] = pub;
    });
    return map;
  }, [menuPublishes.active]);

  const resolveStoreMeta = useCallback(
    (entry) => {
      const payload = entry?.lead || entry || {};
      const storeId =
        payload.storeId || payload.store_id || entry?.storeId || entry?.store_id || "";
      const rawSlug =
        payload.storeSlug ||
        payload.store_slug ||
        payload.store ||
        entry?.storeSlug ||
        entry?.store_slug ||
        entry?.store ||
        "";
      const slug = rawSlug || storeId;
      const storeName =
        payload.storeName ||
        payload.store_name ||
        entry?.storeName ||
        entry?.store_name ||
        "";
      const directUrl =
        payload.publicUrl ||
        payload.storeUrl ||
        payload.store_url ||
        payload.intakeUrl ||
        payload.intake_url ||
        entry?.publicUrl ||
        entry?.storeUrl ||
        entry?.store_url ||
        entry?.intakeUrl ||
        entry?.intake_url ||
        "";
      const pub = storeLookup[`id:${storeId}`] || storeLookup[`slug:${slug}`];
      const hasStoreRef = Boolean(storeName || slug || storeId);
      const name =
        pub?.storeName ||
        pub?.name ||
        storeName ||
        slug ||
        storeId ||
        (hasStoreRef ? "Store" : "Link not found");
      const url =
        directUrl ||
        pub?.publicUrl ||
        (slug ? deriveStoreUrl(slug) : "");
      return { name, url, slug: slug || pub?.storeSlug || "", storeId };
    },
    [storeLookup, deriveStoreUrl]
  );

  const resolveDrr = useCallback((lead) => lead?.drr || lead?.lead?.drr || null, []);

  const normalizeFieldList = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => ({
        label: (item?.label || "").trim(),
        value: (item?.value || "").trim(),
      }))
      .filter((item) => item.label && item.value);
  }, []);

  const parseRawItems = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => {
        const text = String(item || "").trim();
        if (!text) return null;
        const splitIndex = text.indexOf(":");
        if (splitIndex === -1) {
          return { label: "Detail", value: text };
        }
        const label = text.slice(0, splitIndex).trim().replace(/\?$/, "");
        const value = text.slice(splitIndex + 1).trim();
        if (!label || !value) return null;
        return { label, value };
      })
      .filter(Boolean);
  }, []);

  const getFieldByLabel = useCallback((items, label) => {
    if (!label) return null;
    const target = label.toLowerCase();
    return (items || []).find((item) => item.label.toLowerCase() === target) || null;
  }, []);

  const getRoleTitle = useCallback((drr, storeMeta) => {
    return (
      drr?.role_title ||
      (storeMeta?.name && storeMeta.name !== "Store" ? storeMeta.name : "") ||
      "the role"
    );
  }, []);

  const getSignatureLines = useCallback(() => {
    const lines = [];
    if (user?.username) lines.push(user.username);
    if (business?.name) lines.push(business.name);
    if (business?.phone) lines.push(business.phone);
    return lines.length ? lines : ["Team"];
  }, [user, business]);

  const getStoreDisplayName = (storeSlug, storeId) => {
    if (!storeSlug && !storeId) return "Main Intake Link";
    if (storeSlug === "index") return "Main Intake Link";
    return storeSlug || storeId || "Intake Link";
  };

  const buildClientSummary = useCallback(
    ({ name, storeMeta, drr }) => {
      const summaryLines = [];
      const candidateName = name || "Submission";
      const roleName = getRoleTitle(drr, storeMeta);
      const intakeProfile = drr?.intake_profile || null;

      if (intakeProfile) {
        const missingInfo = Array.isArray(intakeProfile.missing_info)
          ? intakeProfile.missing_info.filter((item) => String(item || "").trim())
          : [];
        const keyDetails = [
          ["Route", intakeProfile.route],
          ["Company", intakeProfile.company],
          ["People count", intakeProfile.people_count],
          ["Start timing", intakeProfile.start_timing],
          ["Preferred contact", intakeProfile.preferred_contact],
          ["Brief details", intakeProfile.enquiry_details],
        ].filter(([, value]) => String(value || "").trim());

        summaryLines.push("Hi Team,");
        summaryLines.push("");
        summaryLines.push(`New programme enquiry for ${roleName} (${candidateName}).`);
        summaryLines.push(
          `${intakeProfile.review_status || "Needs review"} with the key follow-up details below.`
        );

        if (keyDetails.length) {
          summaryLines.push("");
          summaryLines.push("Key details");
          keyDetails.forEach(([label, value]) => summaryLines.push(`• ${label}: ${value}`));
        }

        if (missingInfo.length) {
          summaryLines.push("");
          summaryLines.push("Missing details");
          missingInfo.forEach((item) => summaryLines.push(`• ${item}`));
        }

        if ((drr?.potential_blockers || []).length) {
          summaryLines.push("");
          summaryLines.push("Potential blockers");
          drr.potential_blockers.forEach((item) => summaryLines.push(`• ${item}`));
        }

        summaryLines.push("");
        summaryLines.push("Please review the enquiry and confirm the best next step.");
        summaryLines.push("");
        summaryLines.push("Kind regards,");
        summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

        return summaryLines.join("\n");
      }

      summaryLines.push("Hi Team,");
      summaryLines.push("");
      summaryLines.push(
        `Programme enquiry summary for ${roleName} (${candidateName}).`
      );
      summaryLines.push("Short follow-up summary with routing, timing, and key context below.");
      const fields = drr?.fields || {};
      let constraintFields = normalizeFieldList(fields.constraints);
      let availabilityFields = normalizeFieldList(fields.availability_compensation);
      let experienceFields = normalizeFieldList(fields.relevant_experience);
      let otherFields = normalizeFieldList(fields.other);

      const hasStructured =
        constraintFields.length ||
        availabilityFields.length ||
        experienceFields.length ||
        otherFields.length;

      if (!hasStructured) {
        constraintFields = parseRawItems(drr?.key_constraints);
        availabilityFields = parseRawItems(drr?.availability_compensation);
        experienceFields = parseRawItems(drr?.relevant_experience);
        otherFields = parseRawItems(drr?.potential_blockers);
      }

      const keyDetails = [];
      const usedKeys = new Set();
      const fieldKey = (item) => `${item.label}|${item.value}`;
      const addUsed = (item) => item && usedKeys.add(fieldKey(item));

      const location = getFieldByLabel(constraintFields, "Location");
      const relocation = getFieldByLabel(constraintFields, "Relocation");
      if (location) {
        let line = `Based in ${location.value}`;
        if (relocation) {
          const relValue = relocation.value.toLowerCase();
          const relText = relValue.includes("open") || relValue.includes("willing")
            ? "open to relocation"
            : relocation.value;
          line = `${line} (${relText})`;
        }
        keyDetails.push(line);
        addUsed(location);
        addUsed(relocation);
      }

      const rightToWork = getFieldByLabel(constraintFields, "Right to work");
      if (rightToWork) {
        keyDetails.push(`Right to work: ${rightToWork.value}`);
        addUsed(rightToWork);
      }

      const availability = getFieldByLabel(availabilityFields, "Availability");
      if (availability) {
        keyDetails.push(`Availability: ${availability.value}`);
        addUsed(availability);
      }

      const salary = getFieldByLabel(availabilityFields, "Salary expectation");
      if (salary) {
        keyDetails.push(`Salary expectation: ${salary.value}`);
        addUsed(salary);
      }

      const workPref = getFieldByLabel(availabilityFields, "Work preference");
      if (workPref) {
        keyDetails.push(`Work preference: ${workPref.value}`);
        addUsed(workPref);
      }

      constraintFields.forEach((item) => {
        if (!usedKeys.has(fieldKey(item))) {
          keyDetails.push(`${item.label}: ${item.value}`);
        }
      });
      availabilityFields.forEach((item) => {
        if (!usedKeys.has(fieldKey(item))) {
          keyDetails.push(`${item.label}: ${item.value}`);
        }
      });

      const experience = experienceFields.map((item) => {
        if (item.label.toLowerCase() === "experience") {
          return /experience/i.test(item.value)
            ? item.value
            : `${item.value} experience`;
        }
        return `${item.label}: ${item.value}`;
      });

      const notes = otherFields.filter((item) => item.label.toLowerCase() !== "role title");

      if (keyDetails.length) {
        summaryLines.push("");
        summaryLines.push("Key details");
        keyDetails.forEach((item) => summaryLines.push(`• ${item}`));
      }

      if (experience.length) {
        summaryLines.push("");
        summaryLines.push("Experience");
        experience.forEach((item) => summaryLines.push(`• ${item}`));
      }

      if (notes.length) {
        summaryLines.push("");
        summaryLines.push("Additional notes");
        notes.forEach((item) => summaryLines.push(`• ${item.label}: ${item.value}`));
      }

      summaryLines.push("");
      summaryLines.push(
        "Please let me know if you'd like me to arrange the next step or request any more detail."
      );
      summaryLines.push("");
      summaryLines.push("Kind regards,");
      summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

      return summaryLines.join("\n");
    },
    [
      getRoleTitle,
      normalizeFieldList,
      parseRawItems,
      getFieldByLabel,
      getSignatureLines,
    ]
  );

  const buildCandidateSummary = useCallback(
    ({ name, drr }) => {
      const summaryLines = [];
      const candidateName = name || "there";
      const intakeProfile = drr?.intake_profile || null;

      summaryLines.push(`Hi ${candidateName},`);
      summaryLines.push("");
      summaryLines.push(
        intakeProfile
          ? "Thanks for sending your enquiry."
          : "Thanks for completing the apprenticeship enquiry form."
      );
      summaryLines.push("We’ll review your enquiry and confirm the best next step shortly.");
      summaryLines.push("");
      summaryLines.push("Speak soon,");
      summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

      return summaryLines.join("\n");
    },
    [getSignatureLines]
  );

  const handleCopySummary = useCallback((id, text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopiedSummaryId(id);
    window.setTimeout(() => setCopiedSummaryId(null), 1500);
  }, []);

  const getAuthToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = resolveAuthToken(session);
    if (!token) throw new Error("Not authenticated");
    return token;
  }, []);

  const fetchMenuPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${awsConfig.api_url_get_menu}/${business.businessId}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      if (!res.ok) throw new Error("Failed to fetch menu publishes");
      const data = await res.json();
      const publishes = data.publishes || { active: [], archived: [] };
      const activeIds = new Set(publishes.active.map((p) => p.publishedAt));
      const filteredArchived = publishes.archived.filter((p) => !activeIds.has(p.publishedAt));
      setMenuPublishes({
        active: publishes.active,
        archived: filteredArchived,
      });
      if (data.business?.slug) {
        setBusinessSlug(data.business.slug);
      }
    } catch (err) {
      console.error("Error fetching menu publishes", err);
    }
  }, [business?.businessId, getAuthToken]);

  useEffect(() => {
    if (business?.businessId) fetchMenuPublishes();
  }, [business?.businessId, fetchMenuPublishes]);

  useEffect(() => {
    if (!showSlugEdit) {
      setNewSlug(derivedBusinessSlug);
    }
  }, [derivedBusinessSlug, showSlugEdit]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!business?.businessId) return;
      setLoadingOrders(true);
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${awsConfig.api_url_get_order}?businessId=${business.businessId}&status=ALL`,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );
        if (!res.ok) throw new Error("Failed to fetch orders");
        const data = await res.json();
        setOrders(data.orders || []);
      } catch (err) {
        console.error("Error fetching orders", err);
      } finally {
        setLoadingOrders(false);
      }
    };
    if (business?.businessId) fetchOrders();
  }, [business?.businessId, getAuthToken]);

  const updateLeadArchive = async (lead, archived) => {
    const targetId = lead?.orderId || lead?.leadId;
    if (!business?.businessId || !targetId) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(awsConfig.api_url_get_order, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          businessId: business.businessId,
          orderId: targetId,
          archived: Boolean(archived),
        }),
      });
      if (!res.ok) throw new Error("Failed to archive lead");
      const data = await res.json();
      setOrders((prev) =>
        prev.map((o) => (o.orderId === targetId ? data.order : o))
      );
    } catch (err) {
      console.error("Error archiving lead", err);
    }
  };

  const handleSlugUpdate = async () => {
    if (!business?.businessId || !newSlug.trim()) return;
    setSlugLoading(true);
    setSlugError("");
    try {
      const token = await getAuthToken();
      const res = await fetch(SLUG_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          slug: newSlug.trim().toLowerCase(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update slug");
      setBusinessSlug(data.newSlug);
      setShowSlugEdit(false);
      await fetchMenuPublishes();
    } catch (err) {
      setSlugError(err.message);
    } finally {
      setSlugLoading(false);
    }
  };

  const handleStoreSlugEdit = (publishedAt, currentSlug) => {
    setEditingStore(publishedAt);
    setNewStoreSlug(currentSlug || "index");
  };

  const handleStoreSlugUpdate = async (publishedAt) => {
    if (!business?.businessId || !newStoreSlug.trim()) return;
    setSlugLoading(true);
    setSlugError("");
    try {
      const token = await getAuthToken();
      const res = await fetch(`${SLUG_API_URL}?action=store`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          publishedAt: publishedAt,
          newStoreSlug: newStoreSlug.trim().toLowerCase(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update store slug");
      setMenuPublishes((prev) => ({
        ...prev,
        active: prev.active.map((pub) =>
          pub.publishedAt === publishedAt
            ? { ...pub, storeSlug: data.newStoreSlug, htmlUrl: data.newHtmlUrl }
            : pub
        ),
      }));
      setEditingStore(null);
      setNewStoreSlug("");
    } catch (err) {
      setSlugError(err.message);
    } finally {
      setSlugLoading(false);
    }
  };

  const handleDeletePublish = async (pub) => {
    if (!business?.businessId || !pub?.publishedAt) return;
    const confirmDelete = window.confirm(
      `Delete ${pub.storeName || pub.storeSlug || "this intake link"}?`
    );
    if (!confirmDelete) return;
    setDeletingPublish(pub.publishedAt);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${awsConfig.api_url_delete_menu}/${business.businessId}/${pub.publishedAt}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to delete intake link");
      await fetchMenuPublishes();
    } catch (err) {
      console.error("Error deleting intake link", err);
    } finally {
      setDeletingPublish(null);
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const isArchived = Boolean(lead.archived || lead.lead?.archived);
      return showArchivedLeads ? true : !isArchived;
    });
  }, [leads, showArchivedLeads]);

  const callReadyLeads = useMemo(() => filteredLeads, [filteredLeads]);

  const callReadyStats = useMemo(
    () => getCallReadyStats(callReadyLeads, resolveDrr),
    [callReadyLeads, resolveDrr]
  );

  const monthlyScreenedCount = useMemo(() => {
    if (!leads.length) return 0;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return leads.filter((lead) => {
      const createdAt = getLeadTimestamp(lead);
      if (!createdAt) return false;
      const createdDate = new Date(createdAt);
      if (Number.isNaN(createdDate.getTime())) return false;
      return createdDate >= monthStart;
    }).length;
  }, [leads]);

  const recruiterLimit = useMemo(
    () => getRecruiterLimit(activePlanId),
    [activePlanId]
  );

  const screenedUsagePercentage =
    recruiterLimit > 0
      ? Math.min(100, (monthlyScreenedCount / recruiterLimit) * 100)
      : 0;

  const handleSignOut = async () => {
    await signOut();
    onSignOut?.();
  };

  const latestPublish = menuPublishes.active[0];
  const latestPublishUrl =
    latestPublish?.publicUrl ||
    (latestPublish?.storeSlug ? deriveStoreUrl(latestPublish.storeSlug) : "");
  const paymentsEnabled = business?.stripeConnected ?? false;

  return (
    <div className="home-page recruiter-home">
      <div className="home-container">
        <div className="home-logo-wrapper">
          {business?.logoUrl ? (
            <img src={business.logoUrl} alt="Business Logo" className="home-logo" />
          ) : (
            <img src={LogoImg} alt="Layerbound" className="home-logo" />
          )}
        </div>

        <div className="home-header recruiter-header">
          <div className="home-title">
            <Sparkles size={20} className="title-icon" />
            Intake dashboard
          </div>
          <p className="recruiter-subtitle">
            Review submissions, check eligibility, and progress enquiries faster.
          </p>
          {business && planBadge && (
            <div className="plan-indicator">
              <span className={planBadge.className}>
                {planBadge.icon}
                {planBadge.label}
              </span>
              {activePlan?.keyPoints?.length ? (
                <ul className="plan-feature-list">
                  {activePlan.keyPoints.slice(0, 3).map((point) => (
                    <li key={point}>
                      <CheckCircle size={12} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>

        <div className="home-actions recruiter-actions">
          <Link className="btn btn-primary" to="/systems/recruitment">
            Create intake link
          </Link>
          <Link className="btn btn-outline" to="/systems/fitness-booking">
            Create fitness booking link
          </Link>
          {business?.plan === "starter" && (
            <Link className="btn btn-outline" to="/systems/used-car">
              Create car-check link
            </Link>
          )}
          <Link className="btn btn-outline" to="/systems/gatecheck">
            Create Gatecheck link
          </Link>
          <Link className="btn btn-outline" to="/systems/collaboration-milestone-gatecheck">
            Create milestone gatecheck
          </Link>
          <Link className="btn btn-outline" to="/settings">
            Settings
          </Link>
          {!paymentsEnabled && (
            <Link className="btn btn-text" to="/settings">
              Connect Stripe
            </Link>
          )}
          {latestPublishUrl ? (
            <button
              className="btn btn-text"
              onClick={() => window.open(latestPublishUrl, "_blank", "noopener,noreferrer")}
            >
              Open latest intake link
            </button>
          ) : null}
        </div>

        <div className="usage-stats-section recruiter-usage">
          <div className="stats-header">
            <h3 className="usage-stats-title">
              <TrendingUp size={20} />
              Submissions
            </h3>
          </div>
          <div className="usage-stats">
            <div className="usage-stat">
              <div className="usage-stat-header">
                <CheckCircle size={16} />
                <span>Submissions this month</span>
              </div>
              <div className="usage-stat-content">
                <div className="usage-numbers">
                  <span className="usage-current">{monthlyScreenedCount}</span>
                  <span className="usage-separator">/</span>
                  <span className="usage-limit">{recruiterLimit}</span>
                </div>
                <div className="usage-bar-container">
                  <div className="usage-bar">
                    <div
                      className="usage-fill"
                      style={{ width: `${screenedUsagePercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="usage-percentage">
                  {screenedUsagePercentage.toFixed(0)}% used
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="recruiter-links">
          <div className="stats-header">
            <h3 className="usage-stats-title">
              <ExternalLink size={18} />
              Live links
            </h3>
            <div className="recruiter-links-actions">
              <button
                className="btn btn-icon-sm"
                onClick={() => setShowSlugEdit(!showSlugEdit)}
                    title="Edit your custom URL"
                  >
                    <Edit3 size={14} />
                    Edit URL
              </button>
              <button
                className="btn btn-outline"
                onClick={() => setShowArchivedLeads((prev) => !prev)}
              >
                {showArchivedLeads ? "Hide archived" : "Show archived"}
              </button>
            </div>
          </div>
          {showSlugEdit && (
            <div className="slug-editor">
              <div className="slug-input-group">
                <span className="slug-prefix">{MENU_BASE_DISPLAY}/</span>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) =>
                    setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                  }
                  placeholder="your-company-name"
                  className="slug-input"
                  maxLength={50}
                />
              </div>
              {slugError && <div className="slug-error">{slugError}</div>}
              <div className="slug-actions">
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setShowSlugEdit(false);
                    setNewSlug(derivedBusinessSlug);
                    setSlugError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSlugUpdate}
                  disabled={slugLoading || !newSlug.trim() || newSlug === derivedBusinessSlug}
                >
                  {slugLoading ? "Updating all links..." : "Save & Update All"}
                </button>
              </div>
              <div className="slug-help">
                This updates the base URL for all intake links.
              </div>
            </div>
          )}
          {menuPublishes.active.length === 0 ? (
            <div className="empty-links">
              <ExternalLink size={20} />
              <span>No intake links published yet.</span>
            </div>
          ) : (
            <div className="store-links">
              {menuPublishes.active.map((pub) => {
                const storeSlug = pub.storeSlug || pub.slug || "index";
                const storeUrl = pub.publicUrl || deriveStoreUrl(storeSlug);
                const storeName = getStoreDisplayName(pub.storeSlug, pub.storeId);
                const isEditingThisStore = editingStore === pub.publishedAt;
                const isDeleting = deletingPublish === pub.publishedAt;
                const normalizedStoreSlug =
                  pub.storeSlug && pub.storeSlug !== "index" ? pub.storeSlug : "index";
                return (
                  <div key={pub.publishedAt} className="store-link-card">
                    <div className="store-info">
                      <div className="store-name">
                        <Store size={14} />
                        <span>{storeName}</span>
                        {business?.plan === "starter" && (
                          <button
                            className="btn btn-icon-xs"
                            onClick={() => handleStoreSlugEdit(pub.publishedAt, pub.storeSlug)}
                            title="Edit this intake link URL"
                          >
                            <Edit3 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="store-details">
                        {pub.itemsCount ? `${pub.itemsCount} questions` : "Intake flow"} •
                        Published {new Date(pub.publishedAt).toLocaleDateString()}
                      </div>
                    </div>

                    {isEditingThisStore ? (
                      <div className="store-slug-editor">
                        <div className="store-slug-input-group">
                          <span className="slug-prefix">
                            {MENU_BASE_DISPLAY}/{derivedBusinessSlug}/
                          </span>
                          <input
                            type="text"
                            value={newStoreSlug}
                            onChange={(e) =>
                              setNewStoreSlug(
                                e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                              )
                            }
                            placeholder="intake-link"
                            className="store-slug-input"
                            maxLength={30}
                          />
                        </div>
                        {slugError && <div className="slug-error">{slugError}</div>}
                        <div className="store-slug-actions">
                          <button
                            className="btn btn-outline"
                            onClick={() => {
                              setEditingStore(null);
                              setNewStoreSlug("");
                              setSlugError("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleStoreSlugUpdate(pub.publishedAt)}
                            disabled={slugLoading || !newStoreSlug.trim()}
                          >
                            {slugLoading ? "Updating..." : "Update"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="store-url">
                        <a
                          href={storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="menu-link"
                          title={storeUrl}
                        >
                          <ExternalLink size={14} />
                          {`${derivedBusinessSlug}/${normalizedStoreSlug}`}
                        </a>
                      </div>
                    )}

                    {!isEditingThisStore && (
                      <div className="store-actions">
                        <button
                          type="button"
                          className="btn btn-icon-xs"
                          onClick={() => window.open(storeUrl, "_blank", "noopener,noreferrer")}
                          title="Open link"
                        >
                          <ExternalLink size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon-xs danger"
                          onClick={() => handleDeletePublish(pub)}
                          disabled={isDeleting}
                          title="Delete this intake link"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {business?.plan === "free" && menuPublishes.active.length > 0 && (
            <div className="store-plan-notice">
              <AlertTriangle size={14} />
              Upgrade to Starter to create multiple links (up to 3)
            </div>
          )}
        </div>

        <div className="recruiter-queue-header">
          <h3 className="usage-stats-title">
            <CheckCircle size={18} />
            Submissions
          </h3>
          {loadingOrders ? <span className="recruiter-loading">Loading submissions…</span> : null}
        </div>

        <CallReadinessModal
          isOpen
          onClose={() => {}}
          callReadyLeads={callReadyLeads}
          callReadyStats={callReadyStats}
          activeStatusFilter={callReadinessFilter}
          onStatusFilterChange={setCallReadinessFilter}
          resolveDrr={resolveDrr}
          resolveStoreMeta={resolveStoreMeta}
          buildClientSummary={buildClientSummary}
          buildCandidateSummary={buildCandidateSummary}
          handleCopySummary={handleCopySummary}
          copiedSummaryId={copiedSummaryId}
          onArchiveLead={updateLeadArchive}
        />

        <button className="btn btn-text sign-out-btn" onClick={handleSignOut}>
          Sign Out
        </button>

        <div className="home-footer">
          <div className="footer-content">
            © {new Date().getFullYear()} Layerbound. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecruiterHomePage;
